import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useVendorSubscriptionStatus } from '../../services/subscriptionService';
import {
 Bell,
 Mail,
 MessageSquare,
 CalendarDays,
 CreditCard,
 Star,
 Megaphone,
 Loader2,
 Lock,
 AlertCircle,
 ListChecks,
} from 'lucide-react';
import { useToast } from '../../components/shared/Toast';
import Button from '../../components/shared/Button';
import {
 useNotificationSettings,
 useUpdateNotificationSettings,
} from '../../services/userService';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';

/** Vendor marketplace alerts — API rejects updates without an active subscription. */
const SUBSCRIPTION_GATED_KEYS = new Set([
 'booking_updates',
 'payment_updates',
 'message_notifications',
 'review_updates',
]);

const CHANNEL_SETTINGS = [
 {
 key: 'email_notifications',
 label: 'Email',
 description: 'Master switch for email. Booking-related types below also need an active subscription.',
 icon: Mail,
 },
];

const BOOKING_SETTINGS = [
 {
 key: 'booking_updates',
 label: 'Booking activity',
 description:
 'New bookings, schedule changes, reminders, and cancellations for your services.',
 icon: CalendarDays,
 requiresSubscription: true,
 },
 {
 key: 'payment_updates',
 label: 'Payments & payouts',
 description: 'Payment confirmations, refunds, and payout-related updates tied to bookings.',
 icon: CreditCard,
 requiresSubscription: true,
 },
];

const OTHER_SETTINGS = [
 {
 key: 'message_notifications',
 label: 'Client messages',
 description: 'When clients message you through the platform.',
 icon: MessageSquare,
 requiresSubscription: true,
 },
 {
 key: 'review_updates',
 label: 'Reviews',
 description: 'New reviews and replies on your vendor profile.',
 icon: Star,
 requiresSubscription: true,
 },
 {
 key: 'marketing_emails',
 label: 'Product updates & tips',
 description: 'Occasional updates about VendaList features (not booking alerts).',
 icon: Megaphone,
 requiresSubscription: false,
 },
];

function ToggleRow({ setting, checked, disabled, loading, onChange, lockedReason }) {
 const Icon = setting.icon;
 return (
 <div
 className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors ${ disabled ? 'border-border bg-surface-muted/80' : 'border-border bg-surface-muted/50' }`}
 >
 <div className="flex items-center gap-3 min-w-0">
 <div className="w-10 h-10 rounded-lg border border-border bg-surface flex items-center justify-center flex-shrink-0 text-ink-tertiary">
 <Icon className="w-5 h-5" />
 </div>
 <div className="min-w-0">
 <div className="flex items-center gap-2 flex-wrap">
 <p className="font-medium text-ink">{setting.label}</p>
 {lockedReason && (
 <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full">
 <Lock className="w-3 h-3" />
 {lockedReason}
 </span>
 )}
 </div>
 <p className="text-sm text-ink-tertiary mt-0.5">{setting.description}</p>
 </div>
 </div>
 <label
 className={`relative inline-flex items-center flex-shrink-0 ${ disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer' }`}
 >
 <input
 type="checkbox"
 checked={Boolean(checked)}
 onChange={(e) => onChange(setting.key, e.target.checked)}
 disabled={disabled || loading}
 className="sr-only peer"
 />
 <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-300/60 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-surface after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black peer-disabled:opacity-50" />
 </label>
 </div>
 );
}

const VendorPreferences = () => {
 const toast = useToast();
 const [local, setLocal] = useState(null);

 const {
 data: nsResponse,
 isLoading: notificationLoading,
 isError: notificationError,
 error: notificationQueryError,
 refetch: refetchNotifications,
 } = useNotificationSettings({
 staleTime: 60 * 1000,
 retry: 2,
 });

 const { data: statusResponse, isLoading: subscriptionLoading } = useVendorSubscriptionStatus();

 const hasActiveSubscription = Boolean(statusResponse?.data?.has_active_subscription);
 const subscriptionReady = !subscriptionLoading;

 const updateMutation = useUpdateNotificationSettings({
 onError: (err) => {
 const msg =
 err?.response?.data?.message || 'Failed to update notification settings';
 toast.addToast(msg, 'error');
 },
 });

 const server = nsResponse?.data;

 useEffect(() => {
 if (!server) return;
 setLocal({
 email_notifications: Boolean(server.email_notifications),
 booking_updates: Boolean(server.booking_updates),
 payment_updates: Boolean(server.payment_updates),
 review_updates: Boolean(server.review_updates),
 message_notifications: Boolean(server.message_notifications),
 marketing_emails: Boolean(server.marketing_emails),
 });
 }, [server]);

 const handleToggle = useCallback(
 (key, value) => {
 if (!local) return;
 if (SUBSCRIPTION_GATED_KEYS.has(key)) {
 if (!subscriptionReady) return;
 if (!hasActiveSubscription) {
 toast.addToast(
 'An active subscription is required to change booking-related alerts.',
 'error'
 );
 return;
 }
 }

 const previous = local[key];
 setLocal((prev) => ({ ...prev, [key]: value }));

 updateMutation.mutate(
 { [key]: value },
 {
 onSuccess: (res) => {
 if (res?.success) {
 toast.addToast('Notification preferences updated', 'success');
 } else {
 setLocal((prev) => (prev ? { ...prev, [key]: previous } : prev));
 toast.addToast(res?.message || 'Update did not succeed', 'error');
 }
 },
 onError: () => {
 setLocal((prev) => (prev ? { ...prev, [key]: previous } : prev));
 },
 }
 );
 },
 [local, subscriptionReady, hasActiveSubscription, updateMutation, toast]
 );

 const bookingSectionLocked = !subscriptionReady || !hasActiveSubscription;

 const channelBanner = useMemo(() => {
 if (!local) return null;
 const emailOff = !local.email_notifications;
 if (!emailOff) return null;
 return (
 <div className="space-y-2">
 <p className="text-sm text-ink-secondary bg-surface-muted border border-border rounded-xl px-3 py-2">
 Email is off — you will not receive booking or payment messages by email.
 </p>
 <p className="text-sm text-ink-tertiary px-1">
 Dashboard alerts can still appear when booking activity is enabled below and your
 subscription is active.
 </p>
 </div>
 );
 }, [local]);

 const subscriptionBanner =
 subscriptionReady && !hasActiveSubscription ? (
 <div className="flex gap-3 mb-6 p-4 rounded-xl border border-amber-200 bg-amber-50 text-amber-950">
 <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
 <div className="text-sm">
 <p className="font-medium">Subscribe to manage marketplace alerts</p>
 <p className="mt-1 text-amber-900/90">
 Without an active plan, booking, payment, message, and review notification toggles are
 locked. You can still turn email on or off and manage product updates & tips.
 </p>
 <Link
 to="/vendor/subscription"
 className="inline-block mt-3 text-sm font-semibold text-amber-950 underline underline-offset-2 hover:text-amber-800"
 >
 View subscription plans
 </Link>
 </div>
 </div>
 ) : null;

 const gatedLockLabel = !subscriptionReady ? 'Loading…' : 'Subscription required';

 const notificationFetchErrorMessage =
 notificationQueryError?.response?.data?.message ||
 notificationQueryError?.message ||
 'Could not load notification settings.';

 return (
 <div className="space-y-5">
 <VendorPageHeader
 title="Preferences"
 subtitle="Choose which alerts you get by email or in-app; marketplace booking alerts follow your subscription."
 />

 <VendorGuideCallout
 title={VENDOR_PAGE_GUIDES.preferences.title}
 steps={VENDOR_PAGE_GUIDES.preferences.steps}
 icon={ListChecks}
 />

 <div className="product-panel p-4 sm:p-6 lg:p-8">
 <h2 className="text-lg font-semibold text-ink mb-2 flex items-center gap-2">
 <Bell className="w-5 h-5 text-ink-tertiary" />
 Notifications
 </h2>
 <p className="text-sm text-ink-secondary mb-6">
 Marketplace booking alerts require an active vendor subscription (enforced in the app and
 on the server). Product tips do not.
 </p>

 {subscriptionBanner}

 {notificationError && (
 <div className="mb-6 p-4 rounded-xl border border-red-200 bg-red-50 text-red-900 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <span>{notificationFetchErrorMessage}</span>
 <Button type="button" variant="secondary" size="sm" onClick={() => refetchNotifications()}>
 Retry
 </Button>
 </div>
 )}

 {notificationLoading ? (
 <div className="flex items-center justify-center py-16 text-ink-secondary gap-2">
 <Loader2 className="w-6 h-6 animate-spin text-ink" />
 <span>Loading notification settings…</span>
 </div>
 ) : notificationError ? null : !local ? (
 <div className="text-center py-12 text-ink-secondary text-sm">
 No notification settings returned.
 </div>
 ) : (
 <div className="space-y-8">
 <section>
 <h3 className="text-sm font-semibold text-ink uppercase tracking-wide mb-3">
 Email
 </h3>
 <div className="space-y-3">{channelBanner}</div>
 <div className="space-y-3 mt-3">
 {CHANNEL_SETTINGS.map((setting) => (
 <ToggleRow
 key={setting.key}
 setting={setting}
 checked={local[setting.key]}
 disabled={false}
 loading={updateMutation.isPending}
 onChange={handleToggle}
 />
 ))}
 </div>
 </section>

 <section>
 <h3 className="text-sm font-semibold text-ink uppercase tracking-wide mb-3">
 Bookings & payments
 </h3>
 <p className="text-sm text-ink-tertiary mb-3">
 Controls both email (when email is on) and dashboard alerts. Requires an active
 subscription.
 </p>
 <div className="space-y-3">
 {BOOKING_SETTINGS.map((setting) => (
 <ToggleRow
 key={setting.key}
 setting={setting}
 checked={local[setting.key]}
 disabled={bookingSectionLocked}
 loading={updateMutation.isPending}
 onChange={handleToggle}
 lockedReason={bookingSectionLocked ? gatedLockLabel : undefined}
 />
 ))}
 </div>
 </section>

 <section>
 <h3 className="text-sm font-semibold text-ink uppercase tracking-wide mb-3">
 Messages, reviews & updates
 </h3>
 <div className="space-y-3">
 {OTHER_SETTINGS.map((setting) => {
 const gated =
 setting.requiresSubscription &&
 (!subscriptionReady || !hasActiveSubscription);
 return (
 <ToggleRow
 key={setting.key}
 setting={setting}
 checked={local[setting.key]}
 disabled={gated}
 loading={updateMutation.isPending}
 onChange={handleToggle}
 lockedReason={gated ? gatedLockLabel : undefined}
 />
 );
 })}
 </div>
 </section>
 </div>
 )}
 </div>
 </div>
 );
};

export default VendorPreferences;
